/**
 * Per-region cache tag checksums.
 *
 * Each partial response carries `_regionChecksums`, a map of region
 * name to a hash of the region's effective cache tags. On the next
 * navigation the client echoes these back via X-SDX-Region-Tags so
 * the server can skip regions whose tags are still fresh - the
 * server strips those regions from the response body and only the
 * content region always comes through.
 *
 * The store merges incoming checksums rather than replacing them
 * wholesale, so regions that were not part of a given response
 * (e.g., the footer on a pager click) keep their previously-known
 * checksum until something actually invalidates it.
 */

export class RegionChecksums {
  private readonly checksums: Record<string, string> = {};

  /**
   * Captures checksums from the initial page load.
   *
   * Reads the JSON payload the server embeds in its page envelope
   * script - the `<script data-sdx-envelope>` tag `PageRenderer`
   * emits as a preceding sibling of the mount element. The
   * envelope is the only source of region checksums; a widget's
   * own `data-sdx-props-for` script (still emitted on the Twig
   * placeholder path and present in cached pages) describes the
   * widget, not the page regions, and is therefore ignored here
   * even when it is the first match in DOM order.
   *
   * Safe to call when the page has no such payload - non-SDX-engine
   * pages simply leave the store empty.
   */
  captureFromInitialPage(): void {
    const scriptTag = document.querySelector<HTMLScriptElement>(
      'script[type="application/json"][data-sdx-envelope]',
    );
    if (!scriptTag?.textContent) return;
    try {
      const parsed = JSON.parse(scriptTag.textContent) as {
        pageData?: { _regionChecksums?: Record<string, string> };
        _regionChecksums?: Record<string, string>;
      };
      const incoming = parsed.pageData?._regionChecksums ?? parsed._regionChecksums;
      if (incoming) {
        this.merge(incoming);
      }
    }
    catch {
      // Non-JSON or malformed payload is non-fatal; the store just
      // stays empty and every region gets re-rendered until the
      // server populates the cache.
    }
  }

  merge(incoming: Record<string, string>): void {
    for (const [region, checksum] of Object.entries(incoming)) {
      this.checksums[region] = checksum;
    }
  }

  /**
   * Returns the X-SDX-Region-Tags header value, e.g.
   * `header:a3f,footer:9e1,sidebar:042`, or an empty string when
   * no regions have been captured yet.
   */
  getHeader(): string {
    const parts: string[] = [];
    for (const [region, checksum] of Object.entries(this.checksums)) {
      parts.push(`${region}:${checksum}`);
    }
    return parts.join(',');
  }

  isEmpty(): boolean {
    return Object.keys(this.checksums).length === 0;
  }
}
